import { parseHttpError } from '@upme/api-client';
import { type AdminUserDto, hasPlatformAdminRole } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../auth/auth-context';
import { CrmCommentsSection } from '../../crm/components/crm-comments-section';
import {
  useSetCrmManagerPartnerShare,
  useSetInvestor,
  useSetVip,
  useToggleAdminRole,
  useToggleNotifyChannelHealth,
  useToggleNotifyChannelImport,
  useToggleNotifyMembershipLeads,
  useToggleNotifyPhotoModeration,
  useToggleNotifyPromotionTasks,
} from '../mutations';
import { CityModeratorSection } from './city-moderator-section';
import { SendEnergySection } from './send-energy-section';
import { UserSummary } from './user-summary';
import { VipPartnerSection } from './vip-partner-section';

/**
 * Срок VIP живёт в UTC, а поле `<input type="date">` — в календарных днях без
 * зоны. Обе конверсии держим здесь и обе в UTC: иначе введённая дата у админа
 * с отрицательным офсетом возвращалась бы в подсказке предыдущим числом.
 */
function vipUntilToDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** Граница включающая: «до 5 марта» = конец 5 марта, а не его начало. */
function dateInputToVipUntil(draft: string): string | null {
  return draft ? `${draft}T23:59:59.999Z` : null;
}

function todayDateInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatVipUntil(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { timeZone: 'UTC' });
}

function CrmManagerPartnerShareField({ user }: { user: AdminUserDto }) {
  const save = useSetCrmManagerPartnerShare(user.id);
  const [draft, setDraft] = useState(() =>
    user.crmManagerPartnerSharePercent == null ? '' : String(user.crmManagerPartnerSharePercent),
  );
  if (!user.adminRoles.includes('CITY_MODERATOR') && !user.adminRoles.includes('CRM_MANAGER')) {
    return null;
  }
  return (
    <div className="space-y-1 border-t border-border/60 pt-3">
      <Label htmlFor="crm-manager-share">Доля от партнёрской награды, %</Label>
      <p className="text-xs text-muted-foreground">
        От награды партнёрской программы (⚡), не от цены билета. Только новые заявки.
      </p>
      <div className="flex max-w-xs items-center gap-2">
        <Input
          id="crm-manager-share"
          type="number"
          min={0}
          max={100}
          step={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={save.isPending}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={save.isPending}
          onClick={() => {
            const trimmed = draft.trim();
            const percent = trimmed === '' ? null : Number(trimmed);
            if (percent != null && (!Number.isInteger(percent) || percent < 0 || percent > 100)) {
              toast.error('Введите целое число от 0 до 100');
              return;
            }
            void save
              .mutateAsync({ percent })
              .then(() => toast.success('Процент сохранён'))
              .catch(async (err: unknown) => {
                const api = await parseHttpError(err);
                toast.error(api?.message ?? 'Не удалось сохранить процент');
              });
          }}
        >
          Сохранить
        </Button>
      </div>
    </div>
  );
}

function InvestorSwitch({ user }: { user: AdminUserDto }) {
  const setInvestor = useSetInvestor(user.id);
  return (
    <div className="flex items-center gap-3 border-t pt-3">
      <Switch
        id="investor"
        checked={user.isInvestor}
        disabled={user.isFake || setInvestor.isPending}
        onCheckedChange={(value) => {
          void setInvestor
            .mutateAsync({ granted: value })
            .then(() => toast.success(value ? 'Invest выдан' : 'Invest снят'))
            .catch(async (err: unknown) => {
              const api = await parseHttpError(err);
              toast.error(api?.message ?? 'Не удалось изменить статус инвестора');
            });
        }}
        aria-label="Invest"
      />
      <Label htmlFor="investor">Invest</Label>
      {user.isFake && <span className="text-xs text-muted-foreground">фейку статус не выдаётся</span>}
    </div>
  );
}

interface Props {
  user: AdminUserDto;
}

export function UserCard({ user }: Props) {
  const { user: actor, adminRoles: actorRoles } = useAuth();
  const toggle = useToggleAdminRole(user.id);
  const toggleNotify = useToggleNotifyChannelImport(user.id);
  const togglePhotoNotify = useToggleNotifyPhotoModeration(user.id);
  const togglePromotionNotify = useToggleNotifyPromotionTasks(user.id);
  const toggleChannelHealthNotify = useToggleNotifyChannelHealth(user.id);
  const toggleMembershipLeads = useToggleNotifyMembershipLeads(user.id);
  const setVip = useSetVip(user.id);
  // Дата хранится как есть из <input type="date"> (YYYY-MM-DD); в ISO-datetime
  // переводится только в момент отправки — иначе поле дёргалось бы на каждый ввод.
  // Инициализируется текущим сроком: без этого продление у активного VIP молча
  // обнуляло бы срок до «бессрочно».
  const [vipUntilDraft, setVipUntilDraft] = useState(() => vipUntilToDateInput(user.vipUntil));

  async function submitVip(granted: boolean, until: string | null) {
    try {
      await setVip.mutateAsync({ granted, until });
      toast.success(granted ? 'Pro выдан' : 'Pro снят');
    } catch (err) {
      const api = await parseHttpError(err);
      toast.error(api?.message ?? 'Не удалось изменить статус Pro');
    }
  }

  const hasSuperadmin = user.adminRoles.includes('SUPERADMIN');
  const isAdmin = hasPlatformAdminRole(user.adminRoles);
  const isSelf = actor?.id === user.id;
  const disableRevoke = isSelf && hasSuperadmin;
  const canSendEnergy = actorRoles.includes('SUPERADMIN');

  return (
    <div className="space-y-4 max-w-2xl">
      <UserSummary user={user} />

      <div className="rounded-lg border bg-card p-6">
        <CrmCommentsSection target={{ userId: user.id }} />
      </div>

      {canSendEnergy && <SendEnergySection user={user} />}

      <div className="rounded-lg border bg-card p-6 space-y-3">
        <h2 className="font-medium">Pro</h2>
        <div className="flex items-center gap-3">
          <Switch
            id="vip"
            checked={user.isVip}
            // Фейк-аккаунт массовки статуса не получает — бэк ответил бы 422.
            disabled={user.isFake || setVip.isPending}
            onCheckedChange={(value) =>
              // Отзыв срока не несёт: снятие статуса гасит все три колонки.
              void submitVip(value, value ? dateInputToVipUntil(vipUntilDraft) : null)
            }
            aria-label="Pro"
          />
          <Label htmlFor="vip">Pro</Label>
          {user.isFake && <span className="text-xs text-muted-foreground">фейку Pro не выдаётся</span>}
        </div>
        <div className="flex items-center gap-3 border-t pt-3">
          <Label htmlFor="vip-until" className="text-muted-foreground">
            Действует до
          </Label>
          <Input
            id="vip-until"
            type="date"
            className="w-44"
            value={vipUntilDraft}
            // Прошедшую дату сервер отвергает (422) — не даём ввести её вообще.
            min={todayDateInput()}
            disabled={user.isFake || setVip.isPending}
            onChange={(e) => setVipUntilDraft(e.target.value)}
          />
          {user.isVip && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={setVip.isPending}
              onClick={() => void submitVip(true, dateInputToVipUntil(vipUntilDraft))}
            >
              Сохранить срок
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {user.vipUntil ? `сейчас: до ${formatVipUntil(user.vipUntil)}` : 'пусто = бессрочно'}
          </span>
        </div>
        <InvestorSwitch user={user} />
      </div>

      {canSendEnergy && <VipPartnerSection userId={user.id} />}

      <div className="rounded-lg border bg-card p-6 space-y-3">
        <h2 className="font-medium">Права</h2>
        <div className="flex items-center gap-3">
          <Switch
            id="superadmin"
            checked={hasSuperadmin}
            disabled={disableRevoke || toggle.isPending}
            onCheckedChange={(value) => toggle.mutate({ role: 'SUPERADMIN', value })}
            aria-label="SUPERADMIN"
          />
          <Label htmlFor="superadmin">SUPERADMIN</Label>
          {disableRevoke && (
            <span className="text-xs text-muted-foreground">нельзя снять SUPERADMIN с себя</span>
          )}
        </div>

        <CityModeratorSection user={user} />
        <CrmManagerPartnerShareField user={user} />

        {isAdmin && (
          <div className="flex items-center gap-3 border-t pt-3">
            <Switch
              id="notify-channel-import"
              checked={user.notifyChannelImport}
              disabled={toggleNotify.isPending}
              onCheckedChange={(value) => toggleNotify.mutate(value)}
              aria-label="Уведомления о новых черновиках импорта"
            />
            <Label htmlFor="notify-channel-import">Уведомления о новых черновиках импорта в Telegram</Label>
          </div>
        )}

        {isAdmin && (
          <div className="flex items-center gap-3 border-t pt-3">
            <Switch
              id="notify-photo-moderation"
              checked={user.notifyPhotoModeration}
              disabled={togglePhotoNotify.isPending}
              onCheckedChange={(value) => togglePhotoNotify.mutate(value)}
              aria-label="Уведомления о новых фото на модерацию"
            />
            <Label htmlFor="notify-photo-moderation">Уведомления о новых фото знакомств на модерацию</Label>
          </div>
        )}

        {isAdmin && (
          <div className="flex items-center gap-3 border-t pt-3">
            <Switch
              id="notify-promotion-tasks"
              checked={user.notifyPromotionTasks}
              disabled={togglePromotionNotify.isPending}
              onCheckedChange={(value) => togglePromotionNotify.mutate(value)}
              aria-label="Уведомления о задачах продвижения"
            />
            <Label htmlFor="notify-promotion-tasks">Уведомления о задачах продвижения</Label>
          </div>
        )}

        {isAdmin && (
          <div className="flex items-center gap-3 border-t pt-3">
            <Switch
              id="notify-channel-health"
              checked={user.notifyChannelHealth}
              disabled={toggleChannelHealthNotify.isPending}
              onCheckedChange={(value) => toggleChannelHealthNotify.mutate(value)}
              aria-label="Алерты о деградации каналов-источников"
            />
            <Label htmlFor="notify-channel-health">Алерты о деградации каналов-источников</Label>
          </div>
        )}

        {user.adminRoles.includes('SUPERADMIN') && (
          <div className="flex items-center gap-3 border-t pt-3">
            <Switch
              id="notify-membership-leads"
              checked={user.notifyMembershipLeads}
              disabled={toggleMembershipLeads.isPending}
              onCheckedChange={(value) => toggleMembershipLeads.mutate(value)}
              aria-label="Уведомления о заявках на пакеты"
            />
            <Label htmlFor="notify-membership-leads">Уведомления о заявках на пакеты</Label>
          </div>
        )}
      </div>
    </div>
  );
}
